import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardModuleNames, PermissionCode } from '@app/shared/enums';
import { TeaserAsset } from '@app/shared/models/teaser-asset';
import { isJsonString } from '@app/shared/utility';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { filter as _filter, isNil as _isNil, map as _map } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap, timeout } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { OverlayLoaderService } from '../overlay-loader.service';
import { UserMunicipalitiesService } from '../user-municipalities.service';
import { ApiService } from './api.service';
import { ConfigurationService } from './configuration.service';
import { MunicipalityService } from './municipality.service';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';

// Create interfaces to avoid direct imports from sensordaten
interface ISensorType {
  enabled: boolean;
}

interface ISensorTypeDataStoreService {
  sensorTypes$: Observable<ISensorType[]>;
}

@Injectable({
  providedIn: 'root',
})
export class TeaserService extends ApiService {
  private _teaserComponent = '';
  private _teaserPageTitle = '';
  private _teaserType = '';
  private _teaserAssetsStorageUrl = '';
  private _teaserEditorUrl = '';
  private _teaserAssetsByKey!: Observable<any>;
  private _teaserAssetsFromCache!: Observable<any>;
  private _hasEditorPermissions!: boolean;
  private unsubscribe$: Subject<void> = new Subject();

  constructor(
    public http: HttpClient,
    private _router: Router,
    private _cookieService: CookieService,
    private _translate: TranslateService,
    private _configService: ConfigurationService,
    private _userMunicipalitiesService: UserMunicipalitiesService,
    private _municipalityService: MunicipalityService,
    private _authService: AuthService,
    private _overlayLoaderService: OverlayLoaderService,
    private _injector: Injector
  ) {
    super(http);
  }

  setTeaserComponent = (teaserComponent: string): void => {
    this._teaserComponent = teaserComponent;
    switch (teaserComponent) {
      case DashboardModuleNames.sensordaten.toLowerCase():
        this.setSensorTeaserDetails();
        break;
      case DashboardModuleNames.roommonitor.toLowerCase():
        this.setRoomMonitoringTeaserDetails();
        break;
      case DashboardModuleNames.innoLive.toLowerCase():
        this.setInnoLiveTeaserDetails();
        break;
    }
  };

  setSensorTeaserDetails = (): void => {
    this._teaserType = 'sensor';
    this._teaserPageTitle = this._translate.instant('module.sensorData.moduleName');
    this._teaserAssetsStorageUrl = `${environment.tenant}/teasers/sensordaten`;
    this._teaserEditorUrl = 'teaser/sensordaten/editor';
    this._hasEditorPermissions = !this._authService.permissions.SENSOR_TEASER_EDITOR;
    this._teaserAssetsByKey = forkJoin([
      this._configService.cacheByKey('SensorTeaserHeader'),
      this._configService.cacheByKey('SensorTeaserDetail'),
      this._configService.cacheByKey('SensorTeaserAssets'),
      this._configService.cacheByKey('SensorTeaserEmailBtnText'),
    ]).pipe(filter(() => !!this._authService.permissions.SENSOR_DATA));
    this._teaserAssetsFromCache = forkJoin([
      this._configService.fromCache('SensorTeaserHeader'),
      this._configService.fromCache('SensorTeaserDetail'),
      this._configService.fromCache('SensorTeaserAssets'),
      this._configService.fromCache('SensorTeaserEmailBtnText'),
    ]).pipe(filter(() => !!this._authService.permissions.SENSOR_DATA));
  };

  setRoomMonitoringTeaserDetails = (): void => {
    this._teaserType = 'roommonitor';
    this._teaserPageTitle = this._translate.instant('module.roomMonitor.moduleName');
    this._teaserAssetsStorageUrl = `${environment.tenant}/teasers/raummonitoring`;
    this._teaserEditorUrl = 'teaser/raummonitoring/editor';
    this._hasEditorPermissions = !this._authService.permissions.ROOM_MONITORING_EDITOR;
    this._teaserAssetsByKey = forkJoin([
      this._configService.cacheByKey('RoomMonitoringTeaserHeader'),
      this._configService.cacheByKey('RoomMonitoringTeaserDetail'),
      this._configService.cacheByKey('RoomMonitoringTeaserAssets'),
      this._configService.cacheByKey('RoomMonitoringTeaserEmailBtnText'),
    ]).pipe(filter(() => !!this._authService.permissions.ROOM_MONITORING));
    this._teaserAssetsFromCache = forkJoin([
      this._configService.fromCache('RoomMonitoringTeaserHeader'),
      this._configService.fromCache('RoomMonitoringTeaserDetail'),
      this._configService.fromCache('RoomMonitoringTeaserAssets'),
      this._configService.fromCache('RoomMonitoringTeaserEmailBtnText'),
    ]).pipe(filter(() => !!this._authService.permissions.ROOM_MONITORING));
  };

  setInnoLiveTeaserDetails = (): void => {
    this._teaserType = 'innolive';
    this._teaserPageTitle = this._translate.instant('module.innoLive.moduleName');
    this._teaserAssetsStorageUrl = `${environment.tenant}/teasers/innolive`;
    this._teaserAssetsByKey = forkJoin([this._configService.fromCache('InnoLiveAssets')]);
  };

  getTeaserComponent = (): string => {
    return this._teaserComponent;
  };

  saveHeader = (header: string) =>
    this.http.put(this.endpoint(`api`, `teaser`, this._teaserType, `header`), {
      configValue: header,
    });

  saveDetail = (header: string) =>
    this.http.put(this.endpoint(`api`, `teaser`, this._teaserType, `detail`), {
      configValue: header,
    });

  saveEmailBtnText = (value: string) =>
    this.http.put(this.endpoint(`api`, `teaser`, this._teaserType, `email-btn-text`), {
      configValue: value,
    });

  saveAssets = (assets: TeaserAsset[]) => {
    const assetJson = JSON.stringify({
      assets: _map(assets, (a) => ({
        index: a.index,
        asset: a.asset,
        type: a.type,
      })),
    });
    return this.http.put(this.endpoint(`api`, `teaser`, this._teaserType, `assets`), {
      configValue: assetJson,
    });
  };

  cleanupAssets = () => this.http.delete(this.endpoint(`api`, `teaser`, this._teaserType, `cleanup`));

  uploadAsset(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ fileName: string }>(this.directEndpoint(`api`, `teaser`, this._teaserType, `upload`), formData);
  }

  getTeaserPageTitle = (): string => {
    return this._teaserPageTitle;
  };

  getTeaserAssetStorageUrl = (): string => {
    return this._teaserAssetsStorageUrl;
  };

  getTeaserAssetsFromJson(assetJson: string) {
    if (!isJsonString(assetJson)) {
      return [];
    }
    const assetsConfig = JSON.parse(assetJson);
    return _isNil(assetsConfig.assets) ? [] : _filter(assetsConfig.assets, (a) => !!a.asset && a.asset.length > 0);
  }

  getTeaserAssets = (): Observable<any> => {
    return this._teaserAssetsByKey;
  };

  getTeaserAssetsFromCache = (): Observable<any> => {
    return this._teaserAssetsFromCache;
  };

  getTeaserEditorUrl = (): string => {
    return this._teaserEditorUrl;
  };

  clearTeaserAssetsCache = (): void => {
    switch (this._teaserComponent) {
      case DashboardModuleNames.sensordaten.toLowerCase():
        this._configService.purgeByKey('SensorTeaserHeader');
        this._configService.purgeByKey('SensorTeaserDetail');
        this._configService.purgeByKey('SensorTeaserAssets');
        this._configService.purgeByKey('SensorTeaserEmailBtnText');
        break;
      case DashboardModuleNames.roommonitor.toLowerCase():
        this._configService.purgeByKey('RoomMonitoringTeaserHeader');
        this._configService.purgeByKey('RoomMonitoringTeaserDetail');
        this._configService.purgeByKey('RoomMonitoringTeaserAssets');
        this._configService.purgeByKey('RoomMonitoringTeaserEmailBtnText');
    }
  };

  hasEditorPermissions = (): boolean => {
    return this._hasEditorPermissions;
  };

  getTeaserSub = (): Subscription => {
    if (this._teaserComponent.toLowerCase() === DashboardModuleNames.sensordaten.toLowerCase()) {
      // Return a subscription that handles the async loading
      return this._authService.permissionLoadComplete$
        .pipe(
          filter((isLoaded: boolean) => isLoaded),
          switchMap(() => this.loadSensorTypesObservable()),
          timeout(30000), // 30 second timeout to prevent loader from hanging
          takeUntil(this.unsubscribe$)
        )
        .subscribe({
          next: (sensorTypes: ISensorType[]) => {
            const isTeaser = this._authService.permissions[PermissionCode.SENSOR_DATA].teaser === true;
            if (sensorTypes.some((t: ISensorType) => t.enabled) && !isTeaser) {
              this._router.navigate(['/messdaten/sensordaten']);
            }
            // Clear the loader when subscription completes
            this._overlayLoaderService.isLoading = false;
          },
          error: (error) => {
            // Handle error gracefully - sensor functionality might not be available
            // Clear the loader on error as well
            this._overlayLoaderService.isLoading = false;
          },
          complete: () => {
            // Clear the loader when subscription completes
            this._overlayLoaderService.isLoading = false;
          },
        });
    }
    return this.raumMonitoringTeaserSub();
  };

  private loadSensorTypesObservable(): Observable<ISensorType[]> {
    return new Observable<ISensorType[]>((observer) => {
      // Dynamic import to avoid bundling sensordaten in main bundle
      import('@app/sensordaten/services/sensor-type-data-store.service')
        .then((sensorModule) => {
          try {
            // Get the service instance from Angular's injector
            const sensorTypesStore = this._injector.get(sensorModule.SensorTypeDataStoreService);

            // Subscribe to the actual sensor types data, skipping initial empty emission
            let firstEmission = true;
            sensorTypesStore.sensorTypes$.pipe(takeUntil(this.unsubscribe$)).subscribe({
              next: (sensorTypes: any[]) => {
                // Skip the first emission if it's empty, which is common for data stores that start with an empty state
                if (firstEmission && sensorTypes.length === 0) {
                  firstEmission = false;
                  return;
                }
                firstEmission = false;
                // Map to our interface to maintain type safety
                const mappedTypes: ISensorType[] = sensorTypes.map((st) => ({
                  enabled: st.enabled,
                }));
                observer.next(mappedTypes);
                observer.complete();
              },
              error: (error: any) => {
                observer.next([]);
                observer.complete();
              },
            });
          } catch (injectorError) {
            // Service not available in injector, return empty array
            observer.next([]);
            observer.complete();
          }
        })
        .catch((error) => {
          // If sensordaten module fails to load, return empty array
          observer.next([]);
          observer.complete();
        });
    });
  }

  raumMonitoringTeaserSub() {
    return this._userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap((currentMunicipality: MapStoreMunicipality) =>
          this._municipalityService.getByMunicipalityGuid(currentMunicipality.guid).pipe(
            tap((municipality) => {
              this._overlayLoaderService.isLoading = true;
              if (!this._authService.permissions[PermissionCode.ROOM_MONITORING].teaser) {
                window.open(municipality[0].roomMonitoringUrl, '_blank');
                this._router.navigate(['/']);
              }
            })
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => (this._overlayLoaderService.isLoading = false));
  }

  public isOnboardingButtonHidden(): Observable<boolean> {
    return this._authService.permissionLoadComplete$.pipe(
      filter((isLoaded: boolean) => isLoaded),
      map(() => {
        if (!this._authService.permissions[PermissionCode.SENSOR_ONBOARDING]) {
          return true;
        }
        return !!this._authService.permissions[PermissionCode.SENSOR_DATA].teaser;
      })
    );
  }
}
